// 러브 윈즈 올 (Love Wins All) engine types.
// Rules: docs/wireframes/love-wins-all/rule.md (2026-06-24 design-confirmed).
// Commit-reveal hand-ranking (족보) + poker-lite betting. Each round both are
// dealt 3 cards from a 30-card deck (scissors 12 / rock 7 / paper 7 / love 4),
// ante + bet (1-chip unit, 1-raise cap, check/bet/call/raise/fold), then the
// better 3-card rank wins the pot. Rank: Three > Two > One; love is wild;
// same-tier ties broken by RPS head-to-head; One-One = draw. First to 0 chips
// loses. Hidden information: a player's hand is private (opponent sees 3 backs).

export const LOVE_WINS_ALL_PLAYERS = ["p1", "p2"] as const;
/** Non-love kinds in love-wild fill priority order. */
export const RPS_KINDS = ["scissors", "rock", "paper"] as const;
export const CARD_KINDS = ["scissors", "rock", "paper", "love"] as const;

export type LoveWinsAllPlayerId = (typeof LOVE_WINS_ALL_PLAYERS)[number];
export type CardKind = (typeof CARD_KINDS)[number];
export type RpsKind = (typeof RPS_KINDS)[number];

/** Deck composition (30 cards). */
export const DECK_COMPOSITION: Readonly<Record<CardKind, number>> = {
  scissors: 12,
  rock: 7,
  paper: 7,
  love: 4,
};

export const HAND_SIZE = 3;
// Ruleset flags (parameterized — see rule.md §5).
export const LOVE_WINS_ALL_START_CHIPS = 25;
export const LOVE_WINS_ALL_ANTE = 1;
export const LOVE_WINS_ALL_BET_UNIT = 1;
export const LOVE_WINS_ALL_RAISE_CAP = 1;
/** Reshuffle the full deck when fewer than this many cards remain. */
export const RESHUFFLE_THRESHOLD = HAND_SIZE * 2;
export const LOVE_WINS_ALL_ROUND_CAP = 200;

export type LoveWinsAllPhase = "bet" | "game-over";
export type HandRank = "three" | "two" | "one";
export type BetMove = "check" | "bet" | "call" | "raise" | "fold";

export interface ValidationResult {
  readonly valid: boolean;
  readonly error?: string;
  readonly details?: Record<string, unknown>;
}

export interface LoveWinsAllPlayer {
  readonly playerId: LoveWinsAllPlayerId;
  readonly label: string;
}

/** Classified 3-card hand for head-to-head comparison. */
export interface RankInfo {
  readonly rank: HandRank;
  /** Tripled/paired kind (RpsKind), or null for a one / love-triple. */
  readonly kind: RpsKind | null;
  /** Two's kicker (the off-pair card), else null. */
  readonly kicker: RpsKind | null;
  /** Three made of three actual love cards (the top hand). */
  readonly isLoveTriple: boolean;
}

export interface BetAction {
  readonly type: "Bet";
  readonly playerId: LoveWinsAllPlayerId;
  readonly move: BetMove;
}

export type LoveWinsAllAction = BetAction;

/** Public result of a settled round. */
export interface RoundResult {
  readonly round: number;
  readonly leadPlayerId: LoveWinsAllPlayerId;
  /** Revealed hands (public after settle). */
  readonly hands: Readonly<Record<LoveWinsAllPlayerId, readonly CardKind[]>>;
  readonly ranks: Readonly<Record<LoveWinsAllPlayerId, HandRank>>;
  readonly folderId: LoveWinsAllPlayerId | null;
  readonly winnerId: LoveWinsAllPlayerId | "push";
  readonly pot: number;
}

export interface LoveWinsAllState {
  readonly gameId: string;
  readonly players: readonly LoveWinsAllPlayer[];
  readonly phase: LoveWinsAllPhase;
  readonly round: number;
  readonly leadPlayerId: LoveWinsAllPlayerId;
  readonly currentPlayerId: LoveWinsAllPlayerId;
  /** Remaining draw pile (server-only). */
  readonly deck: readonly CardKind[];
  /** This round's hands per player (private). */
  readonly hands: Readonly<Record<LoveWinsAllPlayerId, readonly CardKind[]>>;
  readonly chips: Readonly<Record<LoveWinsAllPlayerId, number>>;
  readonly pot: number;
  readonly contributed: Readonly<Record<LoveWinsAllPlayerId, number>>;
  readonly raisesUsed: number;
  readonly lastAggressorId: LoveWinsAllPlayerId | null;
  /** Seeded PRNG state for deterministic reshuffles (server-only). */
  readonly rngState: number;
  readonly lastRound: RoundResult | null;
  readonly winnerId: LoveWinsAllPlayerId | null;
  readonly lastAction: LoveWinsAllAction | null;
  readonly moveNumber: number;
}

export interface InitGameOptions {
  readonly gameId?: string;
  readonly firstLeadPlayerId?: LoveWinsAllPlayerId;
  /** Seed for the deterministic deck shuffle. */
  readonly seed?: number;
}

// Viewer projection: opponent hand is reduced to a count (cards masked); deck
// and rng state are dropped entirely.
export interface ViewerHand {
  readonly count: number;
  /** Card kinds — null when masked (opponent's un-revealed hand). */
  readonly cards: readonly CardKind[] | null;
}

export interface LoveWinsAllViewerState
  extends Omit<LoveWinsAllState, "deck" | "hands" | "rngState"> {
  readonly hands: Readonly<Record<LoveWinsAllPlayerId, ViewerHand | null>>;
}
